import styled, { useTheme } from 'styled-components'
import { useEffect, useState } from 'react'
import { useTransition, animated } from '@react-spring/web'

export const AnimatedNote = ({
  children,
  arrowHeadposition,
  textPosition,
  show,
  hideArrow,
  inverted,
  chartDimensions,
}: {
  children: React.ReactNode
  arrowHeadposition: [number, number]
  textPosition?: [number, number]
  show?: boolean
  hideArrow?: boolean
  inverted?: boolean
  chartDimensions: [number, number]
}) => {
  const [textRef, setTextRef] = useState<HTMLDivElement | null>(null)
  const [textSize, setTextSize] = useState<[number, number]>([0, 0])
  const svgSize = [300, 300]
  const svgCenter = [svgSize[0] / 2, svgSize[1] / 2]
  const tPos = textPosition || [0, 0]
  const dist = 5
  const txtPos =
    tPos[0] < arrowHeadposition[0] && !hideArrow
      ? [
          tPos[0] - arrowHeadposition[0] - textSize[0],
          tPos[1] - arrowHeadposition[1],
        ]
      : [tPos[0] - arrowHeadposition[0], tPos[1] - arrowHeadposition[1]]

  const transitions = useTransition(show, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  })

  const arrowStartPos = [
    svgCenter[0] + (arrowHeadposition[0] < tPos[0] ? -dist : dist),
    svgCenter[1], // + (arrowHeadposition[1] < tPos[1] ? -dist : dist),
  ]

  const lineStartPos = [
    arrowStartPos[0] + tPos[0] - arrowHeadposition[0],
    arrowStartPos[1] + textSize[1] * 0.5 + txtPos[1],
  ]

  const angle =
    Math.atan2(svgCenter[1] - lineStartPos[1], svgCenter[0] - lineStartPos[0]) +
    (arrowHeadposition[1] < tPos[1] && arrowHeadposition[0] < tPos[0]
      ? Math.PI
      : arrowHeadposition[0] > tPos[0] && arrowHeadposition[1] < tPos[1]
      ? 0
      : arrowHeadposition[0] > tPos[0] && arrowHeadposition[1] > tPos[1]
      ? Math.PI
      : 0)

  const arrowLength = Math.sqrt(
    (svgCenter[0] - lineStartPos[0]) ** 2 +
      (svgCenter[1] - lineStartPos[1]) ** 2
  )

  const rayon = arrowLength * 0.5

  const bezierHandlePos = [
    lineStartPos[0] +
      (svgCenter[0] - lineStartPos[0]) * 0.5 +
      Math.cos(angle + Math.PI / 2) * rayon,
    lineStartPos[1] +
      (svgCenter[1] - lineStartPos[1]) * 0.5 +
      Math.sin(angle + Math.PI / 2) * rayon,
  ]

  useEffect(() => {
    if (textRef) {
      const width = textRef.offsetWidth
      const height = textRef.offsetHeight
      setTextSize([width, height])
    }
  }, [textRef])

  return transitions(
    (style, item) =>
      item && (
        <Container
          style={{
            left: `${arrowHeadposition[0]}px`,
            top: `${arrowHeadposition[1]}px`,
            opacity: style.opacity,
            // transform: style.opacity.to(v => `translate3d(0, ${v * 10}px, 0)`),
          }}
        >
          <Text
            inverted={inverted}
            ref={setTextRef}
            style={{
              textAlign: tPos[0] < arrowHeadposition[0] ? 'right' : 'left',
              transform: `translate(${txtPos[0]}px, ${txtPos[1]}px)`,
            }}
          >
            {children}
          </Text>
          {!hideArrow && (
            <Svg width={svgSize[0]} height={svgSize[1]}>
              <defs>
                <marker
                  id={`arrowHead-${inverted ? 'inverted' : 'normal'}`}
                  orient='auto'
                  markerWidth='10'
                  markerHeight='10'
                  refX='5'
                  refY='5'
                  viewBox='0 0 10 10'
                >
                  <path
                    d='M 0,0 L 5,5 L 0,10'
                    fill='transparent'
                    stroke={inverted ? `#ffffff` : `#121212`}
                  />
                </marker>
              </defs>
              {/* <circle cx={svgCenter[0]} cy={svgCenter[1]} r={2} fill={`red`} />
              <circle
                cx={bezierHandlePos[0]}
                cy={bezierHandlePos[1]}
                r={2}
                fill={`green`}
              />
              <circle
                cx={lineStartPos[0] + (svgCenter[0] - lineStartPos[0]) * 0.5}
                cy={lineStartPos[1] + (svgCenter[1] - lineStartPos[1]) * 0.5}
                r={rayon}
                fill='transparent'
                stroke={`yellow`}
              /> */}
              <Line
                stroke={inverted ? `#ffffff` : `#121212`}
                markerEnd={`url(#arrowHead-${inverted ? 'inverted' : 'normal'})`}
                d={`M${lineStartPos[0]} ${lineStartPos[1]}
                  Q${bezierHandlePos[0]} ${bezierHandlePos[1]}
                  ${svgCenter[0]} ${svgCenter[1]}`}
              />
            </Svg>
          )}
        </Container>
      )
  )
}

const Container = styled(animated.div)`
  position: absolute;
`

const Line = styled.path`
  stroke-width: 1px;
  fill: none;
`

const Text = styled.div<{ inverted?: boolean }>`
  // -webkit-font-smoothing: antialiased;
  // border: 1px solid ${({ theme }) => `blue`};
  position: absolute;
  top: 0;
  left: 0;
  width: 150px;
  line-height: 1.2;
  color: ${({ inverted }) => inverted ? '#ffffff' : '#121212'};
  font-size: 0.85em;
  text-shadow: ${({ inverted, theme }) =>
    inverted
      ? `1px 1px 0 #121212, -1px 1px 0 #121212, -1px -1px 0 #121212, 1px -1px 0 #121212`
      : `1px 1px 0 #ffffff, -1px 1px 0 #ffffff, -1px -1px 0 #ffffff, 1px -1px 0 #ffffff`};

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 0.75em;
    width: 120px;
  }
`

const Svg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  transform: translate(-50%, -50%);
`
